/**
 * React Query hooks for the Claro API.
 *
 * Each hook wraps an API call with caching, refetching, and error handling.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { components } from './schema'
import { searchEmr, searchNotes, searchPatients } from './search'
import { toast } from '@/stores/toast'
import { useAuthStore } from '@/stores/auth'
import { setJurisdiction } from '@/lib/jurisdiction'
import type {
  Note,
  NoteListResponse,
  NoteProfile,
  Suggestion,
  Settings,
  ComparisonResponse,
  PipelineNudgeResponse,
  EvalCompareCreated,
  EvalCompareResponse,
} from './types'
import type { DrugRegisterNotApplied, RecordedAdministration } from './drug-register-accept'
import { reviewOpenedMessage, type RecordReviewSummary } from './record-review-hooks'
import type { NoteDiscardResponse, NoteSaveResponse } from './note-lock'

// --- Notes ---

interface NotesParams {
  clinicId?: number | null
  query?: string
  createdAfter?: string
  createdBefore?: string
  labels?: string[]
}

export function useNotes(params: NotesParams = {}) {
  const labelsParam = params.labels?.length ? params.labels.join(',') : undefined

  return useInfiniteQuery({
    queryKey: ['notes', params.clinicId, params.query, params.createdAfter, params.createdBefore, labelsParam],
    // CLA-1694: the search text goes in the body, never the URL.
    queryFn: async ({ pageParam }) =>
      searchNotes<NoteListResponse>({
        limit: 20,
        q: params.query,
        clinic_id: params.clinicId,
        created_after: params.createdAfter,
        created_before: params.createdBefore,
        labels: labelsParam,
        before_created_at: pageParam?.created_at,
        before_id: pageParam?.id,
      }),
    initialPageParam: null as { created_at: string; id: number } | null,
    getNextPageParam: (lastPage) => lastPage.has_more ? lastPage.cursor : undefined,
    // Poll floor (same doctrine as the iOS drawer, 1.3.14): the WS is the
    // optimisation, the poll is the contract. With the socket down
    // ("Connecting..."), the list otherwise never learns about notes made
    // on other devices/calls. Foreground tabs only.
    refetchInterval: 30_000,
  })
}

export function useNote(
  noteId: number | string | null,
  opts: { includeCareTeam?: boolean } = {},
) {
  // CLA-1103: `includeCareTeam` lets a patient-file surface read a colleague's
  // note (read-only, `is_author: false`). Without it the route is author-only.
  // The own-notes queue (NotesView) deliberately does not pass it: that page
  // edits what it opens. Keyed apart so a care-team read never fills the
  // cache entry an editor reads.
  const includeCareTeam = opts.includeCareTeam === true
  return useQuery({
    queryKey: includeCareTeam ? ['note', noteId, 'care-team'] : ['note', noteId],
    queryFn: () =>
      api.get<Note>(`/notes/${noteId}${includeCareTeam ? '?include_care_team=true' : ''}`),
    enabled: noteId !== null,
  })
}

/**
 * PATCH /notes/:ref draft fields. CLA-760 Slice B — the practitioner's own
 * notes (doctor_notes) and whether they guide generation (doctor_notes_guide).
 */
export function useUpdateNoteDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      noteRef,
      fields,
    }: {
      noteRef: number | string
      fields: { doctor_notes?: string; doctor_notes_guide?: boolean }
    }) => api.patch(`/notes/${noteRef}`, fields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useSaveNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ noteId, note, title, content, baseVersion, acceptSuggestion, amendmentReason }: { noteId: number | string; note?: string; title?: string; content?: Record<string, unknown>; baseVersion?: number | null; acceptSuggestion?: boolean; amendmentReason?: string | null }) => {
      // Optimistic concurrency (claro-elixir #388): echo the version we loaded as
      // `base_version`. If a concurrent save moved the note on, the server returns
      // 409 instead of silently clobbering. The save response carries the new
      // `version` so the caller can advance its base for the next save.
      // CLA-1959: on a locked note the save is kept as an amendment. The reason
      // is optional and never holds the save back; without one the server
      // records "Edited after lock". Ignored by the server on an open note.
      const reason = amendmentReason?.trim() || undefined
      let version: number | undefined
      let saved: NoteSaveResponse | undefined
      if (content !== undefined) {
        // Block editor path: send JSON body with content
        const body: Record<string, unknown> = baseVersion != null ? { content, base_version: baseVersion } : { content }
        // CLA-1865: this save takes the pending AI suggestion into the body, so
        // the server keeps the missing-item flags drawn for it (any other save
        // that clears a pending suggestion clears them; claro-elixir#1645).
        if (acceptSuggestion) body.accept_suggestion = true
        if (reason) body.amendment_reason = reason
        const res = await api.post<NoteSaveResponse>(`/notes/${noteId}/save`, body)
        version = res?.version
        saved = res
      } else if (note !== undefined) {
        // Legacy textarea/iOS path: send form-encoded text
        const form = new FormData()
        form.append('note', note)
        if (baseVersion != null) form.append('base_version', String(baseVersion))
        if (acceptSuggestion) form.append('accept_suggestion', 'true')
        if (reason) form.append('amendment_reason', reason)
        const res = await api.postForm<NoteSaveResponse>(`/notes/${noteId}/save`, form)
        version = res?.version
        saved = res
      }
      if (title !== undefined) {
        const form = new FormData()
        form.append('title', title)
        await api.postForm(`/notes/${noteId}/title`, form)
      }
      return { version, amendment: saved?.amendment, locked: saved?.locked }
    },
    onMutate: async ({ noteId, note, title, content }) => {
      await qc.cancelQueries({ queryKey: ['note', noteId] })
      const previous = qc.getQueryData<Note>(['note', noteId])
      if (previous) {
        qc.setQueryData<Note>(['note', noteId], {
          ...previous,
          ...(note !== undefined ? { note } : {}),
          ...(title !== undefined ? { title } : {}),
          ...(content !== undefined ? { content } : {}),
        })
      }
      return { previous }
    },
    onError: (_err, { noteId }, context) => {
      if (context?.previous) qc.setQueryData(['note', noteId], context.previous)
    },
    onSettled: (_, __, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
    },
  })
}

export function useDiscardNote() {
  const qc = useQueryClient()
  return useMutation({
    // CLA-1959: a locked note's discard may carry a reason (optional; the
    // server records "Discarded after lock (no reason given)" without one).
    // A bare id is the unlocked discard, unchanged.
    mutationFn: (arg: number | string | { noteId: number | string; discardReason?: string | null }) => {
      const { noteId, discardReason } = typeof arg === 'object' ? arg : { noteId: arg, discardReason: undefined }
      const reason = discardReason?.trim()
      return reason
        ? api.post<NoteDiscardResponse>(`/notes/${noteId}/discard`, { discard_reason: reason })
        : api.post<NoteDiscardResponse>(`/notes/${noteId}/discard`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['patient-notes'] })
      toast.info('Note discarded')
    },
  })
}

// --- A/B comparison (CLA-787) ---

/** Blinded A/B comparison for a note. `available: false` when there's none. */
export function useComparison(noteRef: number | string | null) {
  return useQuery({
    queryKey: ['comparison', noteRef],
    queryFn: () => api.get<ComparisonResponse>(`/notes/${noteRef}/comparison`),
    enabled: noteRef !== null && noteRef !== undefined,
    staleTime: 0,
  })
}

interface PickComparisonArgs {
  noteRef: number | string
  winnerSlot?: 'a' | 'b'
  skipped?: boolean
  speedShown?: boolean
  baseVersion?: number | null
}

/** Record the blind pick; the chosen draft is adopted onto the note. */
export function usePickComparison() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteRef, winnerSlot, skipped, speedShown, baseVersion }: PickComparisonArgs) =>
      api.post<Note>(`/notes/${noteRef}/comparison/pick`, {
        winner_slot: winnerSlot,
        skipped: skipped ?? false,
        speed_shown: speedShown ?? false,
        base_version: baseVersion,
        surface: 'web',
      }),
    onSuccess: (_data, { noteRef }) => {
      qc.invalidateQueries({ queryKey: ['note', noteRef] })
      qc.invalidateQueries({ queryKey: ['comparison', noteRef] })
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// --- Preferred-pipeline nudge (CLA-787) ---

/** The blinded "make your preferred style your default?" nudge. */
export function usePipelineNudge() {
  return useQuery({
    queryKey: ['pipeline-nudge'],
    queryFn: () => api.get<PipelineNudgeResponse>('/settings/pipeline-nudge'),
  })
}

/** accept = apply preferred pipeline as default; dismiss = defer; reset = revert to standard. */
export function useDecidePipelineNudge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (action: 'accept' | 'dismiss' | 'reset') =>
      api.post<Settings>('/settings/pipeline-nudge', { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['pipeline-nudge'] })
    },
  })
}

// --- Suggestions ---

export function useSuggestions(noteId: number | string | null) {
  return useQuery({
    queryKey: ['suggestions', noteId],
    queryFn: () => api.get<{ suggestions: Suggestion[] }>(`/notes/${noteId}/suggestions`),
    enabled: noteId !== null,
    select: (data) => data.suggestions,
  })
}

export function useAcceptSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    // `dueAt` (CLA-821) is optional: a follow_up promoted from the end-of-day
    // ritual carries the due date the practitioner chose; existing callers omit
    // it and the body stays empty. Send `due_at` only when provided.
    mutationFn: ({
      noteId,
      suggestionId,
      dueAt,
    }: {
      noteId: number | string
      suggestionId: number
      dueAt?: string | null
    }) =>
      api.post<{ status: string; patient_uuid?: string }>(
        `/notes/${noteId}/suggestions/${suggestionId}/accept`,
        dueAt !== undefined ? { due_at: dueAt } : undefined,
      ),
    onSuccess: (data, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['suggestions', noteId] })
      qc.invalidateQueries({ queryKey: ['notes'] })
      // The ritual's two sections read these — refresh both so an accepted
      // follow-up drops out and the promoted task appears (CLA-821).
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['end-of-day'] })
      if (data.patient_uuid) {
        qc.invalidateQueries({ queryKey: ['patient-medications', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-allergies', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-conditions', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-referrals', data.patient_uuid] })
      }
    },
  })
}

export function useDismissSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, suggestionId }: { noteId: number | string; suggestionId: number }) =>
      api.post(`/notes/${noteId}/suggestions/${suggestionId}/dismiss`),
    onSuccess: (_, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['suggestions', noteId] })
      // A dismissed follow-up should drop out of the end-of-day ritual (CLA-821).
      qc.invalidateQueries({ queryKey: ['end-of-day'] })
    },
  })
}

/**
 * Accept every pending suggestion. Medicine suggestions are recorded, each
 * through the same accept as a single card (CLA-1624): `recorded` lists what
 * was written, `needs_choice` what is waiting on the clinician's answer (left
 * pending), `not_applied` what could not be done and why (left pending).
 */
export interface AcceptAllSuggestionsResult {
  accepted: number
  status: string
  patient_uuid?: string | null
  not_applied?: { suggestion_id: number; suggestion_type: string; reason: string; detail: string }[]
  recorded?: { suggestion_id: number; outcome: string; recorded: RecordedAdministration }[]
  needs_choice?: (DrugRegisterNotApplied & { suggestion_id: number })[]
}

export function useAcceptAllSuggestions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (noteId: number | string) =>
      api.post<AcceptAllSuggestionsResult>(`/notes/${noteId}/suggestions/accept-all`, {
        record_administration: true,
      }),
    onSuccess: (data, noteId) => {
      qc.invalidateQueries({ queryKey: ['suggestions', noteId] })
      qc.invalidateQueries({ queryKey: ['notes'] })
      if (data.recorded?.length) {
        qc.invalidateQueries({ queryKey: ['note', noteId, 'administrations'] })
        qc.invalidateQueries({ queryKey: ['note', noteId, 'suggest-products'] })
        qc.invalidateQueries({ queryKey: ['inventory'] })
      }
      if (data.patient_uuid) {
        qc.invalidateQueries({ queryKey: ['patient-medications', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-allergies', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-conditions', data.patient_uuid] })
        qc.invalidateQueries({ queryKey: ['patient-referrals', data.patient_uuid] })
      }
    },
  })
}

/**
 * A clinical insights run (CLA-1631). The POST only enqueues it, so it comes
 * back `processing` with the run's `job_id`; the outcome arrives later, on the
 * socket as `note_intelligence_finished` or from `useIntelligenceRun`.
 * `status` is `processing`, `complete` or `failed` (treat anything else as
 * processing); `suggestions_count` is set once complete, possibly 0.
 * `job_id` is absent from a backend older than CLA-1631.
 */
export interface IntelligenceRun {
  status: string
  job_id?: number
  suggestions_count?: number | null
}

export function useRunIntelligence() {
  return useMutation({
    mutationFn: (noteId: number | string) =>
      api.post<IntelligenceRun>(`/notes/${noteId}/intelligence`),
  })
}

export const intelligenceRunKey = (noteRef: number | string, jobId: number | null) =>
  ['intelligence-run', noteRef, jobId] as const

/** How often to ask how a run is going while the socket has not said. */
export const INTELLIGENCE_POLL_MS = 3000

/**
 * Where a run has got to. Polls while it is `processing` and `poll` is on;
 * stops by itself once the run is finished. The socket usually answers first
 * (the caller writes its event into this query's cache), so the poll is the
 * floor for a dropped socket, not the main path.
 */
export function useIntelligenceRun(
  noteRef: number | string,
  jobId: number | null,
  { poll }: { poll: boolean },
) {
  return useQuery({
    queryKey: intelligenceRunKey(noteRef, jobId),
    queryFn: () => api.get<IntelligenceRun>(`/notes/${noteRef}/intelligence/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return poll && (status === undefined || isRunInProgress(status)) ? INTELLIGENCE_POLL_MS : false
    },
  })
}

export function isRunInProgress(status: string) {
  return status !== 'complete' && status !== 'failed'
}

// --- Patient search ---

export interface PatientSearchResult {
  uuid: string
  first_name: string
  last_name: string
  preferred_name?: string
  dob?: string
  email?: string
  phone?: string
  /** Suburb only — pick lists never show the street address (CLA-1634). */
  suburb?: string | null
}

interface PatientSearchResponse {
  patients: PatientSearchResult[]
  total: number
}

export function usePatientSearch(query: string) {
  return useQuery({
    queryKey: ['patient-search', query],
    queryFn: () => searchPatients<PatientSearchResponse>({ q: query, limit: 10 }),
    enabled: query.length >= 2,
    select: (data) => data.patients,
  })
}

/**
 * What a note's change of patient did beyond the note (CLA-1686 follow-up,
 * CLA-1693): `booking_link` is what happened to a Timely booking the note was
 * recorded from (`none` when nothing); `record_review` is the review task
 * opened for what the consult had already written onto the patient it left.
 */
export interface NotePatientChange {
  booking_link?: string
  record_review?: RecordReviewSummary | null
}

function afterNotePatientChange(
  qc: ReturnType<typeof useQueryClient>,
  noteId: number | string,
  res: NotePatientChange | undefined,
) {
  qc.invalidateQueries({ queryKey: ['notes'] })
  qc.invalidateQueries({ queryKey: ['note', noteId] })
  if (res?.booking_link && res.booking_link !== 'none') qc.invalidateQueries({ queryKey: ['calendar'] })
  const reviewMessage = reviewOpenedMessage(res?.record_review)
  if (reviewMessage) {
    qc.invalidateQueries({ queryKey: ['tasks'] })
    toast.info('Check the first patient’s record', reviewMessage)
  }
}

export function useLinkPatient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, patientUuid }: { noteId: number | string; patientUuid: string }) => {
      const form = new FormData()
      form.append('patient_uuid', patientUuid)
      return api.postForm<NotePatientChange>(`/notes/${noteId}/patient`, form)
    },
    onSuccess: (res, { noteId }) => afterNotePatientChange(qc, noteId, res),
  })
}

// --- EMR integration ---

export interface EmrPatient {
  external_id: string
  first_name?: string
  last_name?: string
  dob?: string
  sex?: string
  email?: string
  phone?: string
  already_linked: boolean
  claro_patient_uuid?: string
  emr_connection_id?: number
}

export interface EmrSource {
  emr_type: string
  emr_connection_id: number
  patients: EmrPatient[]
  error?: boolean
}

interface EmrSearchResponse {
  sources: EmrSource[]
  patients: EmrPatient[]
  emr_type: string
  emr_connected?: boolean
}

export interface EmrConnection {
  id: number
  clinic_id: number
  emr_type: string
  is_active: boolean
}

interface EmrConnectionsResponse {
  connections: EmrConnection[]
}

export function useEmrConnections(enabled = true) {
  // Keyed by clinic — switching clinics doesn't clear the query cache.
  const activeClinicId = useAuthStore((s) => s.activeClinicId)
  return useQuery({
    queryKey: ['emr', 'connections', activeClinicId],
    queryFn: () => api.get<EmrConnectionsResponse>('/emr/connections'),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useEmrSearch(query: string, enabled = false) {
  return useQuery({
    queryKey: ['emr', 'search', query],
    queryFn: () => searchEmr<EmrSearchResponse>({ name: query }),
    enabled: enabled && query.length >= 2,
  })
}

/**
 * `POST /emr/link`: the Claro patient (matched or made from the EMR's
 * details) in the standard `Patient` shape plus `emr_linked`, claro-elixir
 * #1780. Never a credential or token.
 */
export function useEmrLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { external_id: string; emr_connection_id?: number }) =>
      api.post<components['schemas']['EmrLinkedPatient']>('/emr/link', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patients'] })
      qc.invalidateQueries({ queryKey: ['patient-search'] })
    },
  })
}

export function useEmrPushNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { note_ref: string | number }) =>
      api.post<{ status?: string; already_pushed?: boolean; external_ref?: string }>('/emr/push-note', data),
    onSuccess: (_, { note_ref }) => {
      qc.invalidateQueries({ queryKey: ['note', note_ref] })
      qc.invalidateQueries({ queryKey: ['emr', 'pending'] })
    },
  })
}

export function useEmrPending() {
  return useQuery({
    queryKey: ['emr', 'pending'],
    queryFn: () => api.get<EmrPendingResponse>('/emr/pending'),
  })
}

export interface EmrPendingNote {
  id: number
  uuid: string
  title: string
  created_at: string
  status: string
  patient_uuid: string
  patient_name: string
  emr_type: string
  emr_connection_id: number
}

interface EmrPendingResponse {
  notes: EmrPendingNote[]
  count: number
}

export function useUnlinkPatient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (noteId: number | string) => {
      const form = new FormData()
      form.append('patient_uuid', '')
      return api.postForm<NotePatientChange>(`/notes/${noteId}/patient`, form)
    },
    onSuccess: (res, noteId) => afterNotePatientChange(qc, noteId, res),
  })
}

// --- Labels ---

export function useLabelSuggestions(entityType: string, enabled: boolean) {
  return useQuery({
    queryKey: ['label-suggestions', entityType],
    queryFn: () => api.get<string[]>(`/labels/suggest?entity_type=${encodeURIComponent(entityType)}`),
    enabled,
  })
}

export function useAddNoteLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, label }: { noteId: number | string; label: string }) => {
      const form = new FormData()
      form.append('label', label)
      return api.postForm<{ id: number; label: string; type: string }>(`/notes/${noteId}/labels`, form)
    },
    onMutate: async ({ noteId, label }) => {
      await qc.cancelQueries({ queryKey: ['note', noteId] })
      const previous = qc.getQueryData<Note>(['note', noteId])
      if (previous) {
        qc.setQueryData<Note>(['note', noteId], {
          ...previous,
          labels: [
            ...previous.labels,
            { id: -Date.now(), label, type: 'custom', is_auto: false, colour: null },
          ],
        })
      }
      return { previous }
    },
    onError: (_err, { noteId }, context) => {
      if (context?.previous) qc.setQueryData(['note', noteId], context.previous)
    },
    onSettled: (_, __, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
    },
  })
}

export function useRemoveNoteLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, labelId }: { noteId: number | string; labelId: number }) =>
      api.delete(`/notes/${noteId}/labels/${labelId}`),
    onMutate: async ({ noteId, labelId }) => {
      await qc.cancelQueries({ queryKey: ['note', noteId] })
      const previous = qc.getQueryData<Note>(['note', noteId])
      if (previous) {
        qc.setQueryData<Note>(['note', noteId], {
          ...previous,
          labels: previous.labels.filter((l) => l.id !== labelId),
        })
      }
      return { previous }
    },
    onError: (_err, { noteId }, context) => {
      if (context?.previous) qc.setQueryData(['note', noteId], context.previous)
    },
    onSettled: (_, __, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
    },
  })
}

// --- Clinic assignment ---

export function useSetNoteClinic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, clinicId }: { noteId: number | string; clinicId: number | null }) => {
      const form = new FormData()
      if (clinicId !== null) {
        form.append('clinic_id', String(clinicId))
      }
      return api.postForm<{ clinic_id: number | null; clinic_name: string }>(`/notes/${noteId}/clinic`, form)
    },
    onSuccess: (_, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
    },
  })
}

// --- Appointment link ---

export function useSetNoteAppointment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, appointmentId }: { noteId: number | string; appointmentId: number | null }) => {
      const form = new FormData()
      if (appointmentId !== null) {
        form.append('appointment_id', String(appointmentId))
      }
      return api.postForm<{ appointment_id: number | null }>(`/notes/${noteId}/appointment`, form)
    },
    onSuccess: (_, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
      qc.invalidateQueries({ queryKey: ['calendar', 'appointments'] })
      qc.invalidateQueries({ queryKey: ['calendar', 'appointment-notes'] })
    },
  })
}

// --- Note Profiles ---

export function useProfiles() {
  return useQuery({
    queryKey: ['profiles'],
    queryFn: () => api.get<{ profiles: NoteProfile[] }>('/profiles'),
  })
}

export function useCreateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<NoteProfile>) => api.post<NoteProfile>('/profiles', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }) },
  })
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Partial<NoteProfile>) =>
      api.put<NoteProfile>(`/profiles/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }) },
  })
}

export function useDeleteProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/profiles/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profiles'] }) },
  })
}

export function useSetNoteProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteId, profileId }: { noteId: number | string; profileId: number | null }) => {
      const form = new FormData()
      if (profileId !== null) {
        form.append('profile_id', String(profileId))
      }
      return api.postForm<{ profile_id: number | null }>(`/notes/${noteId}/profile`, form)
    },
    onSuccess: (_, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
    },
  })
}

// --- Profile mappings (appointment type → profile) ---

interface ProfileMapping {
  id: number
  appointment_type: string
  profile_id: number
  profile_name: string
  clinic_id?: number
  org_id?: number
}

export function useProfileMappings() {
  return useQuery({
    queryKey: ['profile-mappings'],
    queryFn: () => api.get<{ mappings: ProfileMapping[] }>('/profiles/mappings'),
  })
}

export function useSaveProfileMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { appointment_type: string; profile_id: number | null }) =>
      api.post('/profiles/mappings', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profile-mappings'] }) },
  })
}

export function useResolveProfileMapping(appointmentType: string | undefined) {
  return useQuery({
    queryKey: ['profile-resolve', appointmentType],
    queryFn: () => api.get<{ profile_id: number | null }>(`/profiles/resolve?appointment_type=${encodeURIComponent(appointmentType!)}`),
    enabled: !!appointmentType,
  })
}

// --- Reprocess ---

export function useReprocessNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      noteId,
      profileId,
      instructions,
      transcriptionProvider,
    }: {
      noteId: number | string
      profileId?: number | null
      // One-off freetext guidance for this regeneration (CLA-726). Clinical
      // type, format, and detail are now driven by the note profile or
      // auto-detected by the backend — no longer sent from the client.
      instructions?: string
      // Escape hatch (ADR-020): re-transcribe the retained audio on a NAMED
      // traditional engine before regenerating — the lever when the
      // transcript itself looks wrong. Replaces the transcript server-side.
      transcriptionProvider?: string
    }) => {
      const form = new FormData()
      if (profileId) form.append('profile_id', String(profileId))
      if (instructions && instructions.trim()) form.append('instructions', instructions.trim())
      if (transcriptionProvider) form.append('transcription_provider', transcriptionProvider)
      return api.postForm<Note>(`/notes/${noteId}/reprocess`, form)
    },
    onSuccess: (_, { noteId }) => {
      qc.invalidateQueries({ queryKey: ['notes'] })
      qc.invalidateQueries({ queryKey: ['note', noteId] })
      qc.invalidateQueries({ queryKey: ['suggestions', noteId] })
    },
  })
}

// --- Create note ---

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => {
      const form = new FormData()
      form.append('note', '')
      form.append('title', '')
      // "+ New note" is a typed draft, not a capture: `composing` keeps it
      // list-visible without the "Claro is listening" recording state, so the
      // body editor (and / blocks) mount immediately (ADR-026 typed-note path).
      form.append('intent', 'compose')
      return api.postForm<Note>(`/notes`, form)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// --- Note media ---

export function useNoteMedia(
  noteId: number | string | null,
  opts: { includeCareTeam?: boolean } = {},
) {
  // CLA-1103: a colleague's note's attachments (read-only) need the opt-in.
  const includeCareTeam = opts.includeCareTeam === true
  return useQuery({
    queryKey: includeCareTeam ? ['note-media', noteId, 'care-team'] : ['note-media', noteId],
    queryFn: () =>
      api.get<{ media: import('./types').MediaItem[]; total: number }>(
        `/notes/${noteId}/media${includeCareTeam ? '?include_care_team=true' : ''}`,
      ),
    enabled: noteId !== null,
    select: (data) => data.media,
  })
}

export function useLinkNoteMedia(noteId: number | string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mediaUuid: string) => {
      const fd = new FormData()
      fd.append('media_uuid', mediaUuid)
      return api.postForm(`/notes/${noteId}/media/link`, fd)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-media', noteId] }),
  })
}

export function useUnlinkNoteMedia(noteId: number | string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mediaUuid: string) => {
      const fd = new FormData()
      fd.append('media_uuid', mediaUuid)
      return api.postForm(`/notes/${noteId}/media/unlink`, fd)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-media', noteId] }),
  })
}

// --- Settings ---

// Auto-write the browser-detected timezone once per session when the server
// reports the user's timezone is a silent cascade/realm fallback
// (`timezone_explicit: false`) rather than an explicit choice. Without this,
// e.g. a US-realm user outside Eastern time gets America/New_York on every
// server-rendered output until they manually set a timezone in Settings.
// Fire-and-forget: failures are silent, and the guard flag ensures at most
// one attempt per page session. Never fires when the flag is true (explicit
// value) or absent (older backend without the flag).
let timezoneAutoWriteAttempted = false

/** Test-only: reset the once-per-session auto-write guard. */
export function _resetTimezoneAutoWriteForTests() {
  timezoneAutoWriteAttempted = false
}

function maybeAutoWriteTimezone(
  settings: Settings,
  patchCache: (updated: Settings) => void,
) {
  if (timezoneAutoWriteAttempted) return
  if (settings.timezone_explicit !== false) return
  let detected: string | undefined
  try {
    detected = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return
  }
  if (!detected) return
  timezoneAutoWriteAttempted = true
  // The server validates via DateTime.shift_zone and returns the updated
  // settings response — patch the cache so Settings shows the new value.
  api
    .post<Settings>('/settings', { timezone: detected })
    .then((updated) => patchCache(updated))
    .catch(() => {
      // Silent by design — the user keeps the fallback timezone and can
      // still set one manually in Settings.
    })
}

export function useSettings() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const settings = await api.get<Settings>('/settings')
      // Persist the jurisdiction block for the lib/jurisdiction label helpers
      // (CLA-859 P2). setJurisdiction ignores absent/malformed blocks (old backend).
      setJurisdiction(settings.jurisdiction)
      maybeAutoWriteTimezone(settings, (updated) =>
        qc.setQueryData(['settings'], updated),
      )
      return settings
    },
  })
}

// --- Drug register / administration ---

export interface AdministrationRecord {
  id: number
  quantity: number
  units?: string
  balance_after: number
  recorded_at: string
  dose_details?: { sites?: string } | null
  notes?: string | null
  medicine: { id: number; brand_name: string; generic_name: string; unit_type?: string | null }
  batch: { id: number; batch_number: string; expiry_date: string; quantity_remaining: number } | null
  administered_by: { id: number; name?: string }
  prescribed_by: { id: number; name?: string }
  authority_item_id?: number | null
}

export interface ProductSuggestion {
  medicine_id: number
  brand_name: string
  generic_name: string
  unit_type?: string | null
  batches: {
    batch_id: number; batch_number: string; expiry_date: string
    quantity_remaining: number; quantity_received: number; units?: string
    is_open: boolean
  }[]
  recommended_batch: {
    batch_id: number; batch_number: string; expiry_date: string
    quantity_remaining: number; units?: string; is_open: boolean
  }
}

export function useNoteAdministrations(noteRef: string | null) {
  return useQuery({
    queryKey: ['note', noteRef, 'administrations'],
    queryFn: () => api.get<{ administrations: AdministrationRecord[] }>(`/notes/${noteRef}/administrations`),
    enabled: noteRef !== null,
  })
}

export function useSuggestProducts(noteRef: string | null) {
  return useQuery({
    queryKey: ['note', noteRef, 'suggest-products'],
    queryFn: () => api.get<{ suggestions: ProductSuggestion[] }>(`/notes/${noteRef}/suggest-products`),
    enabled: noteRef !== null,
  })
}

export interface AdministerTransaction {
  id: number
  quantity: number
  units: string | null
  balance_after: number
  batch_id: number | null
  vial_id: number | null
  vial_needs_reconstitution: boolean
  vial_context: {
    id: number
    vial_number: number | null
    units_total: string | number
    brand_name: string
    strength: string | null
    unit_type: string | null
    batch_number: string | null
    recommended_concentration_units_per_ml: number | null
  } | null
}

export interface AdministerResponse {
  status: string
  transactions: AdministerTransaction[]
}

export function useAdminister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteRef, data }: { noteRef: string; data: Record<string, unknown> }) =>
      api.post<AdministerResponse>(`/notes/${noteRef}/administer`, data),
    onSuccess: (_, { noteRef }) => {
      qc.invalidateQueries({ queryKey: ['note', noteRef, 'administrations'] })
      qc.invalidateQueries({ queryKey: ['note', noteRef, 'suggest-products'] })
      qc.invalidateQueries({ queryKey: ['inventory'] })
      qc.invalidateQueries({ queryKey: ['patient'] })
    },
  })
}

export function useRemoveAdministration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ noteRef, txnId }: { noteRef: string; txnId: number }) =>
      api.delete(`/notes/${noteRef}/administrations/${txnId}`),
    onSuccess: (_, { noteRef }) => {
      qc.invalidateQueries({ queryKey: ['note', noteRef, 'administrations'] })
      qc.invalidateQueries({ queryKey: ['note', noteRef, 'suggest-products'] })
      qc.invalidateQueries({ queryKey: ['inventory'] })
    },
  })
}

// --- Pipeline eval-compare (admin-only builder tool) ---
//
// Blind-then-reveal, in-the-moment comparison of how a note generates via
// different pipeline variants. The default comparison (empty body) is the
// transcription A/B — Gemini-audio vs AssemblyAI, same note model. Polling
// (not WS) for MVP: the GET hook refetches while the run is `pending` and stops
// once it's `ready`/`failed`. This is one of the few places polling is right —
// the work is an external generation job with no per-step WS event.

/** Kick off a comparison on a note. Empty body → backend's default A/B. */
export function useCreateEvalCompare(noteRef: number | string) {
  return useMutation({
    mutationFn: (variants?: unknown[]) =>
      api.post<EvalCompareCreated>(
        `/notes/${noteRef}/eval-compare`,
        variants?.length ? { variants } : {},
      ),
  })
}

/**
 * Fetch a comparison, polling while it's still generating. `refetchInterval`
 * returns `false` (stop) once the run is no longer `pending`. Enabled only once
 * we have an id to fetch.
 */
export function useEvalCompare(noteRef: number | string, id: number | string | null) {
  return useQuery({
    queryKey: ['eval-compare', noteRef, id],
    queryFn: () => api.get<EvalCompareResponse>(`/notes/${noteRef}/eval-compare/${id}`),
    enabled: id !== null && id !== undefined,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? 1500 : false,
  })
}

/** Reveal a comparison — unblinds each slot's variant + metadata. */
export function useRevealEvalCompare(noteRef: number | string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number | string) =>
      api.post<EvalCompareResponse>(`/notes/${noteRef}/eval-compare/${id}/reveal`),
    onSuccess: (data, id) => {
      qc.setQueryData(['eval-compare', noteRef, id], data)
    },
  })
}

interface PickEvalCompareArgs {
  id: number | string
  winnerSlot: string
  reason?: string
}

/** Record the verdict (also reveals). Returns the revealed comparison. */
export function usePickEvalCompare(noteRef: number | string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, winnerSlot, reason }: PickEvalCompareArgs) =>
      api.post<EvalCompareResponse>(`/notes/${noteRef}/eval-compare/${id}/pick`, {
        winner_slot: winnerSlot,
        ...(reason ? { reason } : {}),
      }),
    onSuccess: (data, { id }) => {
      qc.setQueryData(['eval-compare', noteRef, id], data)
    },
  })
}
